import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Map, Play, RefreshCw } from 'lucide-react';

const STATUS_STYLE = {
  strong: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  growing: 'bg-amber-100 text-amber-800 border-amber-200',
  limited: 'bg-red-100 text-red-700 border-red-200',
};
const STATUS_LABEL = { strong: 'Strong', growing: 'Growing', limited: 'Limited' };

const AdminCoverage = () => {
  const [cov, setCov] = useState(null);
  const [engine, setEngine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, e] = await Promise.all([
        axios.get(`${API_URL}/admin/coverage`),
        axios.get(`${API_URL}/admin/resource-collection/status`),
      ]);
      setCov(c.data); setEngine(e.data);
    } catch (err) { toast.error('Could not load (admin only).'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { document.title = 'Resource Coverage | Admin'; load(); }, [load]);

  const runBatch = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API_URL}/admin/resource-collection/run-batch`, { limit: 25 });
      setEngine(res.data.status);
      toast.success(`Processed ${res.data.result.processed} tasks · ${res.data.result.remaining} remaining`);
      load();
    } catch (e) { toast.error('Could not run batch.'); } finally { setRunning(false); }
  };

  const enqueue = async () => {
    try { const res = await axios.post(`${API_URL}/admin/resource-collection/enqueue-all`); setEngine(res.data.status); toast.success(res.data.message); }
    catch (e) { toast.error('Could not enqueue.'); }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="text-sm text-gray-500 hover:text-primary flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Admin Dashboard</Link>
        <h1 className="font-serif text-3xl font-bold text-primary flex items-center gap-2 mb-6" data-testid="coverage-title"><Map className="w-7 h-7 text-secondary" /> National Resource Coverage</h1>

        {loading ? <div className="py-12 text-center text-gray-500">Loading…</div> : cov && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              {['strong', 'growing', 'limited'].map((k) => (
                <div key={k} className={`rounded-2xl border p-5 text-center ${STATUS_STYLE[k]}`} data-testid={`summary-${k}`}>
                  <div className="text-3xl font-extrabold">{cov.summary[k]}</div>
                  <div className="text-sm font-semibold">{STATUS_LABEL[k]} Coverage</div>
                </div>
              ))}
            </div>

            {/* Collection engine control */}
            {engine && (
              <div className="bg-white rounded-2xl border p-5 mb-6" data-testid="engine-panel">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="font-bold text-primary flex items-center gap-2"><RefreshCw className="w-5 h-5" /> Resource Collection Engine</h2>
                    <p className="text-sm text-gray-500">Resumable background collector · runs daily 9:00 AM UTC · new finds go to the Review Queue.</p>
                    <p className="text-xs text-gray-400 mt-1">Last run: {engine.last_run ? new Date(engine.last_run).toLocaleString() : 'not yet'}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={enqueue} data-testid="engine-enqueue">Re-scan states</Button>
                    <Button onClick={runBatch} disabled={running} className="bg-primary text-white" data-testid="engine-run">
                      <Play className="w-4 h-4 mr-1" /> {running ? 'Running…' : 'Run batch now'}
                    </Button>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>{engine.done} of {engine.total} tasks processed</span>
                    <span>{engine.percent}%</span>
                  </div>
                  <div className="bg-gray-100 rounded-full h-3 overflow-hidden">
                    <div className="h-3 bg-secondary rounded-full transition-all" style={{ width: `${engine.percent}%` }} data-testid="engine-progress" />
                  </div>
                </div>
              </div>
            )}

            {/* Per-state grid */}
            <div className="bg-white rounded-2xl border overflow-hidden" data-testid="coverage-grid">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs text-gray-500">
                    <th className="p-3">State</th><th className="p-3">Status</th><th className="p-3">Local/County</th>
                    <th className="p-3">Statewide</th><th className="p-3">Counties</th><th className="p-3">Categories</th>
                  </tr>
                </thead>
                <tbody>
                  {cov.states.map((s) => (
                    <tr key={s.state} className="border-t hover:bg-gray-50" data-testid={`coverage-row-${s.state.replace(/\s/g, '-')}`}>
                      <td className="p-3 font-semibold text-primary">
                        <Link to={`/community/resources?state=${encodeURIComponent(s.state)}`} className="hover:underline">{s.state}</Link>
                      </td>
                      <td className="p-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS_STYLE[s.status]}`}>{STATUS_LABEL[s.status]}</span></td>
                      <td className="p-3 text-gray-700">{s.local}</td>
                      <td className="p-3 text-gray-700">{s.statewide}</td>
                      <td className="p-3 text-gray-700">{s.counties}</td>
                      <td className="p-3 text-gray-700">{s.categories_covered}<span className="text-gray-400">/{s.categories_total}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminCoverage;
